import React, { useState, useMemo, useCallback } from 'react'
import { useHistory, generatePath } from 'react-router-dom'

import {
  Button,
  FlexboxGrid,
  Nav,
  Navbar,
  Panel,
  Table,
  Modal,
  Form,
  Input,
  SelectPicker,
  InputNumber,
  Notification,
  toaster,
  IconButton
} from 'rsuite'

import { AddOutline, CreateOutline, TrashOutline } from 'react-ionicons'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'

import {
  ROUTE_SETTINGS_TOPOLOGY_COUNTRY_CREATE,
  ROUTE_SETTINGS_TOPOLOGY_COUNTRY_EDIT,
  ROUTE_SETTINGS_TOPOLOGY_REGION_CREATE,
  ROUTE_SETTINGS_TOPOLOGY_REGION_EDIT,
  ROUTE_SETTINGS_TOPOLOGY_CITY_CREATE,
  ROUTE_SETTINGS_TOPOLOGY_CITY_EDIT
} from '@@partner:config/routes'

import { useFetchTopology, TopologyCountry, TopologyRegion, TopologyCity } from '@@partner:graphql/query/topology.gql'
import { useDeleteCountry } from '@@partner:graphql/mutation/topology/deleteCountry.gql'
import { useDeleteRegion } from '@@partner:graphql/mutation/topology/deleteRegion.gql'
import { useDeleteCity } from '@@partner:graphql/mutation/topology/deleteCity.gql'

const { Column, HeaderCell, Cell } = Table

type TabType = 'countries' | 'regions' | 'cities'

export default function TopologyPage () {
  const history = useHistory()
  const [ activeTab, setActiveTab ] = useState<TabType>('countries')
  const [ selectedCountryId, setSelectedCountryId ] = useState<string | null>(null)
  const [ selectedRegionId, setSelectedRegionId ] = useState<string | null>(null)

  const { data, loading, error, refetch } = useFetchTopology()

  const [ deleteCountry ] = useDeleteCountry()
  const [ deleteRegion ] = useDeleteRegion()
  const [ deleteCity ] = useDeleteCity()

  const countries = useMemo(
    () => data?.topology?.countries ?? [],
    [ data?.topology?.countries ]
  )

  const countryOptions = useMemo(
    () => countries.map(c => ({ label: c.name, value: c.id })),
    [ countries ]
  )

  const regions = useMemo(() => {
    if (!selectedCountryId) {
      return countries.flatMap(c => c.regions.map(r => ({ ...r, countryName: c.name })))
    }
    const country = countries.find(c => c.id === selectedCountryId)
    return country?.regions.map(r => ({ ...r, countryName: country.name })) ?? []
  }, [ countries, selectedCountryId ])

  const regionOptions = useMemo(
    () => regions.map(r => ({ label: `${r.name} (${r.countryName})`, value: r.id })),
    [ regions ]
  )

  const cities = useMemo(() => {
    if (selectedRegionId) {
      const region = regions.find(r => r.id === selectedRegionId)
      return region?.cities.map(c => ({ ...c, regionName: region.name })) ?? []
    }
    return regions.flatMap(r => r.cities.map(c => ({ ...c, regionName: r.name })))
  }, [ regions, selectedRegionId ])

  const handleDeleteCountry = useCallback(async (id: string) => {
    if (!confirm('Удалить страну? Это возможно только если у неё нет регионов.')) return
    
    try {
      await deleteCountry({ variables: { id } })
      toaster.push(
        <Notification type="success" header="Успех">Страна удалена</Notification>,
        { placement: 'topEnd' }
      )
      refetch()
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }, [ deleteCountry, refetch ])

  const handleDeleteRegion = useCallback(async (id: string) => {
    if (!confirm('Удалить регион? Это возможно только если у него нет городов.')) return
    
    try {
      await deleteRegion({ variables: { id } })
      toaster.push(
        <Notification type="success" header="Успех">Регион удалён</Notification>,
        { placement: 'topEnd' }
      )
      refetch()
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }, [ deleteRegion, refetch ])

  const handleDeleteCity = useCallback(async (id: string) => {
    if (!confirm('Удалить город? Это возможно только если к нему не привязаны магазины.')) return
    
    try {
      await deleteCity({ variables: { id } })
      toaster.push(
        <Notification type="success" header="Успех">Город удалён</Notification>,
        { placement: 'topEnd' }
      )
      refetch()
    }
    catch (err: any) {
      toaster.push(
        <Notification type="error" header="Ошибка">{err.message}</Notification>,
        { placement: 'topEnd' }
      )
    }
  }, [ deleteCity, refetch ])

  if (error) {
    return (
      <div className="TopologyPage">
        <h1 className="u-heading">Топология</h1>
        <SettingsTopMenu />
        <GraphQLCommonErrorDisplay title="Ошибка загрузки топологии" error={error} />
      </div>
    )
  }

  return (
    <div className="TopologyPage">
      <h1 className="u-heading">Топология</h1>
      <SettingsTopMenu />

      <Panel shaded className="mt-4">
        <Nav appearance="subtle" activeKey={activeTab} onSelect={(key) => setActiveTab(key as TabType)}>
          <Nav.Item eventKey="countries">Страны ({countries.length})</Nav.Item>
          <Nav.Item eventKey="regions">Регионы ({regions.length})</Nav.Item>
          <Nav.Item eventKey="cities">Города ({cities.length})</Nav.Item>
        </Nav>

        {/* Countries Tab */}
        {activeTab === 'countries' && (
          <div className="mt-4">
            <Navbar appearance="subtle" className="mb-4">
              <Nav>
                <Nav.Item
                  icon={<AddOutline />}
                  onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY_COUNTRY_CREATE)}
                >
                  Добавить страну
                </Nav.Item>
              </Nav>
            </Navbar>

            <Table
              data={countries}
              loading={loading}
              autoHeight
              rowHeight={50}
            >
              <Column width={80} align="center">
                <HeaderCell>ID</HeaderCell>
                <Cell dataKey="id" />
              </Column>

              <Column flexGrow={1}>
                <HeaderCell>Название</HeaderCell>
                <Cell dataKey="name" />
              </Column>

              <Column width={80}>
                <HeaderCell>ISO2</HeaderCell>
                <Cell dataKey="ISO2" />
              </Column>

              <Column width={80}>
                <HeaderCell>ISO3</HeaderCell>
                <Cell dataKey="ISO3" />
              </Column>

              <Column width={120}>
                <HeaderCell>Телефонный код</HeaderCell>
                <Cell dataKey="phone_code" />
              </Column>

              <Column width={100}>
                <HeaderCell>Регионов</HeaderCell>
                <Cell>
                  {(rowData: TopologyCountry) => rowData.regions?.length ?? 0}
                </Cell>
              </Column>

              <Column width={150} fixed="right">
                <HeaderCell>Действия</HeaderCell>
                <Cell>
                  {(rowData: TopologyCountry) => (
                    <FlexboxGrid>
                      <IconButton
                        appearance="subtle"
                        icon={<CreateOutline />}
                        size="sm"
                        onClick={() => history.push(
                          generatePath(ROUTE_SETTINGS_TOPOLOGY_COUNTRY_EDIT, { countryId: rowData.id })
                        )}
                      />
                      <IconButton
                        appearance="subtle"
                        icon={<TrashOutline color="red" />}
                        size="sm"
                        onClick={() => handleDeleteCountry(rowData.id)}
                      />
                    </FlexboxGrid>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        )}

        {/* Regions Tab */}
        {activeTab === 'regions' && (
          <div className="mt-4">
            <Navbar appearance="subtle" className="mb-4">
              <Nav>
                <Nav.Item
                  icon={<AddOutline />}
                  onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY_REGION_CREATE)}
                >
                  Добавить регион
                </Nav.Item>
              </Nav>
              <Nav pullRight>
                <SelectPicker
                  placeholder="Фильтр по стране"
                  data={countryOptions}
                  value={selectedCountryId}
                  onChange={setSelectedCountryId}
                  cleanable
                  style={{ width: 200 }}
                />
              </Nav>
            </Navbar>

            <Table
              data={regions}
              loading={loading}
              autoHeight
              rowHeight={50}
            >
              <Column width={80} align="center">
                <HeaderCell>ID</HeaderCell>
                <Cell dataKey="id" />
              </Column>

              <Column flexGrow={1}>
                <HeaderCell>Название</HeaderCell>
                <Cell dataKey="name" />
              </Column>

              <Column width={200}>
                <HeaderCell>Страна</HeaderCell>
                <Cell dataKey="countryName" />
              </Column>

              <Column width={100}>
                <HeaderCell>Городов</HeaderCell>
                <Cell>
                  {(rowData: TopologyRegion & { countryName: string }) => rowData.cities?.length ?? 0}
                </Cell>
              </Column>

              <Column width={150} fixed="right">
                <HeaderCell>Действия</HeaderCell>
                <Cell>
                  {(rowData: TopologyRegion) => (
                    <FlexboxGrid>
                      <IconButton
                        appearance="subtle"
                        icon={<CreateOutline />}
                        size="sm"
                        onClick={() => history.push(
                          generatePath(ROUTE_SETTINGS_TOPOLOGY_REGION_EDIT, { regionId: rowData.id })
                        )}
                      />
                      <IconButton
                        appearance="subtle"
                        icon={<TrashOutline color="red" />}
                        size="sm"
                        onClick={() => handleDeleteRegion(rowData.id)}
                      />
                    </FlexboxGrid>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        )}

        {/* Cities Tab */}
        {activeTab === 'cities' && (
          <div className="mt-4">
            <Navbar appearance="subtle" className="mb-4">
              <Nav>
                <Nav.Item
                  icon={<AddOutline />}
                  onClick={() => history.push(ROUTE_SETTINGS_TOPOLOGY_CITY_CREATE)}
                >
                  Добавить город
                </Nav.Item>
              </Nav>
              <Nav pullRight>
                <SelectPicker
                  placeholder="Фильтр по региону"
                  data={regionOptions}
                  value={selectedRegionId}
                  onChange={setSelectedRegionId}
                  cleanable
                  style={{ width: 250 }}
                />
              </Nav>
            </Navbar>

            <Table
              data={cities}
              loading={loading}
              autoHeight
              rowHeight={50}
            >
              <Column width={80} align="center">
                <HeaderCell>ID</HeaderCell>
                <Cell dataKey="id" />
              </Column>

              <Column flexGrow={1}>
                <HeaderCell>Название</HeaderCell>
                <Cell dataKey="name" />
              </Column>

              <Column width={200}>
                <HeaderCell>Регион</HeaderCell>
                <Cell dataKey="regionName" />
              </Column>

              <Column width={200}>
                <HeaderCell>Координаты</HeaderCell>
                <Cell>
                  {(rowData: TopologyCity & { regionName: string }) => {
                    const geo = rowData.defaultGeoPoint
                    if (!geo) return '-'
                    const lat = geo.latitude ?? geo.geo_lat
                    const lon = geo.longitude ?? geo.geo_lon
                    return lat && lon ? `${lat.toFixed(4)}, ${lon.toFixed(4)}` : '-'
                  }}
                </Cell>
              </Column>

              <Column width={150} fixed="right">
                <HeaderCell>Действия</HeaderCell>
                <Cell>
                  {(rowData: TopologyCity) => (
                    <FlexboxGrid>
                      <IconButton
                        appearance="subtle"
                        icon={<CreateOutline />}
                        size="sm"
                        onClick={() => history.push(
                          generatePath(ROUTE_SETTINGS_TOPOLOGY_CITY_EDIT, { cityId: rowData.id })
                        )}
                      />
                      <IconButton
                        appearance="subtle"
                        icon={<TrashOutline color="red" />}
                        size="sm"
                        onClick={() => handleDeleteCity(rowData.id)}
                      />
                    </FlexboxGrid>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  )
}
